
import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ArrowUp } from "lucide-react";
import {
    motion,
    useReducedMotion,
    useScroll,
    useTransform
} from "framer-motion";

import logo from "../../../assets/images/logo.png";
import "./Styles.css";

const footerGroups = [
    {
        title: "PLATFORM",
        links: [
            ["Provably Fair Engine", "/provably-fair-engine"],
            ["Infrastructure", "/info/multi-tenant/infrastructure"],
            ["Risk Management", "/risk-management"],
            ["Original Games", "/originals-custom"]
        ]
    },
    {
        title: "OPERATORS",
        links: [
            ["Startup Operators", "/solutions/startup-operators"],
            ["Sweepstakes", "/solutions/sweepstakes"],
            ["Commercial Models", "/pricing"],
            ["Book Demo", "/book-demo"]
        ]
    },
    {
        title: "COMPANY",
        links: [
            ["About Ethosino", "/about"],
            ["Documentation", "/request-documentation"],
            ["Contact Sales", "/book-demo"]
        ]
    }
];

export default function SalesFooter({ previewImage }) {
    const footerRef = useRef(null);
    const reduceMotion = useReducedMotion();

    const { scrollYProgress } = useScroll({
        target: footerRef,
        offset: ["start end", "end start"]
    });

    const imageY = useTransform(
        scrollYProgress,
        [0, 1],
        reduceMotion ? [0, 0] : [22, -22]
    );

    const scrollTop = () => {
        window.scrollTo({
            top: 0,
            behavior: reduceMotion ? "auto" : "smooth"
        });
    };

    return (
        <footer
            ref={footerRef}
            id="ethosino-sales-footer-v4"
            className="esf-footer"
        >
            <div className="esf-shell">

                {/* COMPACT PRODUCT CTA */}
                <motion.section
                    className="esf-showcase"
                    initial={reduceMotion ? false : { opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.65 }}
                >
                    <div className="esf-showcase-copy">
                        <span className="esf-kicker">
                            <span className="esf-status-dot" />
                            BUILT FOR CASINO OPERATORS
                        </span>

                        <h2>
                            Your brand.
                            <span>Our infrastructure.</span>
                        </h2>

                        <p>
                            Launch a branded casino experience with games,
                            wallets, player management, and operator controls
                            connected in one platform.
                        </p>

                        <div className="esf-showcase-actions">
                            <a
                                href="https://demo.ethosino.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="esf-primary-button"
                            >
                                EXPLORE PLATFORM
                                <ArrowUpRight size={17} />
                            </a>

                            <Link
                                to="/book-demo"
                                className="esf-text-link"
                            >
                                Book a demo <ArrowRight size={15} />
                            </Link>
                        </div>
                    </div>

                    {previewImage && (
                        <div className="esf-showcase-visual">
                            <motion.div
                                className="esf-preview-frame"
                                style={{ y: imageY }}
                            >
                                <div className="esf-preview-top">
                                    <span className="esf-preview-dots">
                                        <i /><i /><i />
                                    </span>
                                    <span>ETHOSINO / PLATFORM PREVIEW</span>
                                </div>

                                <img
                                    src={previewImage}
                                    alt="Ethosino casino platform preview"
                                    loading="lazy"
                                    decoding="async"
                                />
                            </motion.div>
                        </div>
                    )}
                </motion.section>

                {/* NAVIGATION */}
                <div className="esf-main">
                    <div className="esf-brand">
                        <Link to="/" className="esf-logo">
                            <img src={logo} alt="Ethosino" />
                        </Link>

                        <p>
                            Turnkey multi-tenant casino infrastructure
                            for operators building their own gaming brands.
                        </p>

                        <span className="esf-brand-tag">
                            <span className="esf-status-dot" />
                            OPERATOR INFRASTRUCTURE
                        </span>
                    </div>

                    <nav
                        className="esf-navigation"
                        aria-label="Footer navigation"
                    >
                        {footerGroups.map(group => (
                            <div
                                className="esf-nav-group"
                                key={group.title}
                            >
                                <h3>{group.title}</h3>

                                {group.links.map(([label, path]) => (
                                    <Link key={path} to={path}>
                                        {label}
                                        <ArrowUpRight size={13} />
                                    </Link>
                                ))}
                            </div>
                        ))}
                    </nav>
                </div>

                {/* BOTTOM */}
                <div className="esf-bottom">
                    <span>
                        © {new Date().getFullYear()} Ethosino.
                        All rights reserved.
                    </span>

                    <div className="esf-bottom-links">
                        <Link to="/about">Company</Link>
                        <Link to="/request-documentation">
                            Documentation
                        </Link>
                        <Link to="/book-demo">Contact</Link>
                    </div>

                    <button
                        type="button"
                        onClick={scrollTop}
                        className="esf-back-top"
                        aria-label="Back to top"
                    >
                        <ArrowUp size={16} />
                    </button>
                </div>
            </div>
        </footer>
    );
}
